import React, { useEffect, useState } from "react";
import StandardModal, { StandardModalBody, StandardModalFooter } from '../../../../components/modales/StandardModal';
import { Box, Typography, TextField, Button, Checkbox, FormControlLabel, FormGroup, Divider } from "@mui/material";
import { Route } from "../../../../models/route";
import { ExpenseType } from "../../../../models/expense-type";
import { EmptyState } from '@routes-plan/ui';
import { useT } from '../../../../i18n';

interface RouteDetailModalProps {
  open: boolean;
  handleClose: () => void;
  officeRoutes: Route[];
  routeCheckbox: { [key: number]: boolean };
  handleCheckboxChange: (routeId: number) => void;
  route: Route | null;
  expenses: ExpenseType[];
  setValue: (value: number, expenseId: number) => void;
  isEditable: boolean;
}

const RouteDetailModal: React.FC<RouteDetailModalProps> = ({
  open,
  handleClose,
  officeRoutes,
  routeCheckbox,
  handleCheckboxChange,
  route,
  expenses,
  setValue,
  isEditable,
}) => {
  const t = useT();
  const [selectedExpenses, setSelectedExpenses] = useState<number[]>([]);
  const [tempValues, setTempValues] = useState<Record<number, string>>({});

  const applyTempValues = () => {
    Object.entries(tempValues).forEach(([expenseId, rawValue]) => {
      const value = rawValue.trim();

      if (!value) {
        setValue(0, Number(expenseId));
        return;
      }

      const numericValue = Number(value);
      if (Number.isNaN(numericValue) || numericValue <= 0) {
        setValue(0, Number(expenseId));
        return;
      }

      setValue(numericValue, Number(expenseId));
    });
  };

  const handleModalClose = () => {
    applyTempValues();
    handleClose();
  };

  const handleBlur = (expenseId: number, event: React.FocusEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.trim();

    if (!rawValue) {
      setTempValues((prev) => ({ ...prev, [expenseId]: "" }));
      setValue(0, expenseId);
      return;
    }

    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue) || numericValue <= 0) {
      setTempValues((prev) => ({ ...prev, [expenseId]: "" }));
      setValue(0, expenseId);
      return;
    }

    setValue(numericValue, expenseId);
    setTempValues((prev) => ({ ...prev, [expenseId]: rawValue }));
  };

  const handleInputChange = (expenseId: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTempValues((prev) => ({
      ...prev,
      [expenseId]: value,
    }));
  };

  const handleExpenseSelection = (expenseId: number) => {
    setSelectedExpenses((prev) => {
      const isSelected = prev.includes(expenseId);

      if (isSelected) {
        setValue(0, expenseId);
        return prev.filter((id) => id !== expenseId);
      } else {
        return [...prev, expenseId];
      }
    });
  };

  useEffect(() => {
    if (!open) {
      setSelectedExpenses([]);
      return;
    }

    const selectedIds = expenses
      .filter((expense) => Number(expense.value || 0) > 0)
      .map((expense) => expense.id);

    setSelectedExpenses(selectedIds);
  }, [open, expenses]);

  useEffect(() => {
    if (!open) {
      setTempValues({});
      return;
    }

    setTempValues((prev) => {
      const updated: Record<number, string> = {};

      selectedExpenses.forEach((id) => {
        if (prev[id] !== undefined) {
          updated[id] = prev[id];
          return;
        }

        const expense = expenses.find((exp) => exp.id === id);
        if (expense && expense.value !== undefined && expense.value !== null && expense.value !== 0) {
          updated[id] = expense.value.toString();
        } else {
          updated[id] = "";
        }
      });

      const prevKeys = Object.keys(prev);
      const hasChanges =
        prevKeys.length !== selectedExpenses.length ||
        selectedExpenses.some((id) => updated[id] !== prev[id]);

      return hasChanges ? updated : prev;
    });
  }, [open, selectedExpenses, expenses]);

  const getDisplayValue = (expense: ExpenseType) => {
    if (tempValues[expense.id] !== undefined) {
      return tempValues[expense.id];
    }

    if (expense.value !== undefined && expense.value !== null && expense.value !== 0) {
      return expense.value.toString();
    }

    return "";
  };

  return (
    <StandardModal
      open={open}
      onClose={handleModalClose}
      title={t('expenses.detailTitle')}
      maxWidth={500}
      composed
    >
      <StandardModalBody>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('expenses.selectedRoute')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {route?.name || t('expenses.noRouteSelected')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t('expenses.selectExpenses')}
        </Typography>
        <FormGroup>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(180px, 100%), 1fr))', columnGap: 1 }}>
            {expenses.map((expense) => (
              <Box key={expense.id} sx={{ minWidth: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedExpenses.includes(expense.id)}
                      onChange={() => handleExpenseSelection(expense.id)}
                      disabled={!isEditable}
                    />
                  }
                  label={expense.name}
                />
              </Box>
            ))}
          </Box>
        </FormGroup>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {selectedExpenses.length > 0 ? (
            selectedExpenses.map((expenseId) => {
              const expense = expenses.find((exp) => exp.id === expenseId);
              if (!expense) return null;

              return (
                <Box key={expense.id} sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{expense.name}</Typography>
                  <Box>
                    <TextField
                      fullWidth
                      label={t('expenses.value')}
                      placeholder="1000"
                      type="number"
                      required
                      size="small"
                      value={getDisplayValue(expense)}
                      onChange={(event) => handleInputChange(expense.id, event as React.ChangeEvent<HTMLInputElement>)}
                      onBlur={(event) => handleBlur(expense.id, event as React.FocusEvent<HTMLInputElement>)}
                      inputProps={{ maxLength: 20, min: 0 }}
                      disabled={!isEditable || !selectedExpenses.includes(expense.id)}
                    />
                  </Box>
                </Box>
              );
            })
          ) : (
            <EmptyState dense title={t('expenses.noExpensesSelected')} />
          )}
        </Box>

      </StandardModalBody>
      <StandardModalFooter>
        <Button variant="contained" onClick={handleModalClose}>
          {t('common.close')}
        </Button>

      </StandardModalFooter>
    </StandardModal>
  );
};

export default RouteDetailModal;
